import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import { ColDef, GridApi, SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ConfigProvider, Input, Pagination, PaginationProps, Select, TreeSelect } from 'antd';
import viVN from 'antd/locale/vi_VN';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IDetailGroup } from '../../../../../../api/groupCustomerV2/groupCustomerV2Api';
import { getListUserAccount, IAccount } from '../../../../../../api/groupPermission/groupPermissionApi';
import { NodataTable } from '../../../../../../components/NoDataTable';
import useDebounce from '../../../../../../hook/useDebounce';
import useLoading from '../../../../../../hook/useLoading';
import { selectorInfoUser } from '../../../../../../store/slices/infoUserSlice';
import { dataPagination, formatPrice } from '../../../../../../until';
import '../../../style.scss';
import { GROUPTYPE } from '../../AddGroup/AddNewGroup';
import { useCustomerGroupContext } from '../../../indexV2';
import { formatNumber } from '../../../../../../services/mixin';
import { PERMISSION } from '../../AddGroup/Component/AG_Decentralization';


// Cấu hình cột
export const columnDefs: ColDef[] = [
    { headerName: "Mã NV", field: "code", minWidth: 200 },
    { headerName: "Họ tên", field: "fullname", minWidth: 200 },
    { headerName: "Email", field: "email", minWidth: 200 },
    { headerName: "Đơn vị", field: "unit", minWidth: 200 },
    { headerName: "Quyền", field: "permission", minWidth: 200 },

    // { headerName: "CCCD/CMND", field: "cccd", minWidth: 200 },
    // { headerName: "Mã số thuế", field: "maSoThue", flex: 1 },
    // { headerName: "Ngày sinh", field: "ngaySinh", flex: 1 },
    // { headerName: "Giới tính", field: "gioiTinh", flex: 1 },
    // { headerName: "Số điện thoại", field: "soDienThoai", minWidth: 200 },
];


export interface ISelectedUserAccount {
    adminId: number;
    permissionType: PERMISSION
}
interface IDataDefaulFilterList {
    search?: string;
    listUnit?: number[]
}

const dataDefaulFilterList: IDataDefaulFilterList = {
    listUnit: [],
};

interface props {
    inforGroup?: IDetailGroup
    setSelectedIdAccount: React.Dispatch<React.SetStateAction<ISelectedUserAccount[]>>
}

const EG_Decentralization: React.FC<props> = ({
    inforGroup,
    setSelectedIdAccount
}) => {
    const pushLoading = useLoading()
    const { groupPermission } = useSelector(selectorInfoUser)
    const {
        // sLUnit, sLCategoryCar
        sLNationwide,
    } = useCustomerGroupContext();


    const isRestoringSelection = useRef(false);
    const gridApiRef = useRef<GridApi | null>(null);

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [tabPermission, setTabPermission] = useState(PERMISSION.VIEW)

    const [dataFilterList, setDataFilterList] = useState<IDataDefaulFilterList>(dataDefaulFilterList)

    const [selectedIds, setSelectedIds] = useState<Set<Number>>(new Set());

    const [listUserAccount, setListUserAccount] = useState<IAccount[]>([]);

    const [permissionByUserId, setPermissionByUserId] = useState<
        Record<string, PERMISSION>
    >({});

    const permissionLabelMap: Record<PERMISSION, string> = {
        [PERMISSION.VIEW]: "Xem",
        [PERMISSION.EDIT]: "Sửa",
        [PERMISSION.DELETE]: "Sửa, Xóa",
    };


    const gListUserAccount = useDebounce(async (limit: number = 10, pageNumber: number = 1) => {
        pushLoading(true)
        const { listUnit, search } = dataFilterList
        const result = await getListUserAccount(
            limit, pageNumber, listUnit, undefined,
            search, groupPermission ?? undefined, 1,
        );
        if (result.status) {
            setListUserAccount(result.data)
            setPagination({ current: pageNumber, total: result.total, pageSize: limit });

        } else {
            // pushToast(result.message, "error", 2000);
        }
        pushLoading(false)
    }, 1000)


    const rowData = useMemo(() => {
        return (listUserAccount ?? []).map((item: any) => {
            const userId = String(item.id);
            const permission = permissionByUserId[userId];

            return {
                ...item,
                permission:
                    permission !== undefined
                        ? permissionLabelMap[permission]
                        : undefined,
            };
        });
    }, [listUserAccount, permissionByUserId]);

    //------------------------------------
    // Định danh duy nhất cho mỗi row
    const getRowId = useCallback((p: { data: any }) => String(p.data.id), []);

    // --- Grid ready ---
    const onGridReady = useCallback((params: { api: GridApi }) => {
        gridApiRef.current = params.api;
    }, []);

    // --- Selection change ---
    const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
        if (isRestoringSelection.current) return;

        const api = event.api;
        const selectedNodes = api.getSelectedNodes().filter(n => n.data);
        const selectedIdsOnPage = selectedNodes.map(n => Number(n.data.id));

        const currentPageIds = (listUserAccount ?? []).map((row: any) => Number(row.id));

        // --- Tạo bản sao của selectedIds hiện tại ---
        const nextSet = new Set(selectedIds);

        // --- Xác định phần tử được thêm và bị xóa trên trang HIỆN TẠI ---
        const added: Number[] = [];
        const removed: Number[] = [];

        // Kiểm tra từng ID trên trang hiện tại
        for (const id of currentPageIds) {
            const isCurrentlySelected = selectedIdsOnPage.includes(id);
            const wasPreviouslySelected = selectedIds.has(id);

            if (isCurrentlySelected && !wasPreviouslySelected) {
                // ID mới được chọn
                added.push(id);
                nextSet.add(id);
            } else if (!isCurrentlySelected && wasPreviouslySelected) {
                // ID bị bỏ chọn
                removed.push(id);
                nextSet.delete(id);
            }
        }

        // console.log('Added:', added, 'Removed:', removed);

        // 👉 Cập nhật danh sách selected account
        handleChangeSelectedUserAccount(added, removed);

        // Cập nhật quyền cho row vừa được chọn
        if (added.length > 0) {
            setPermissionByUserId(prev => {
                const next = { ...prev }
                added.forEach(id => { next[String(id)] = tabPermission })
                return next
            })
        }
        // Xóa quyền khi bỏ chọn
        if (removed.length > 0) {
            setPermissionByUserId(prev => {
                const next = { ...prev }
                removed.forEach(id => { delete next[String(id)] })
                return next
            })
        }

        setSelectedIds(nextSet);

    }, [selectedIds, listUserAccount, tabPermission]);
    //------------------------------------

    // --- Cập nhật danh sách account được chọn ---
    const handleChangeSelectedUserAccount = (itemsAdd: Number[], itemsRemove: Number[]) => {
        setSelectedIdAccount((prevList) => {
            // Nếu có add
            if (itemsAdd.length > 0) {
                const newUsers = itemsAdd.map((id) => ({
                    adminId: Number(id),
                    permissionType: tabPermission,
                }));
                return [...prevList, ...newUsers];
            }

            // Nếu có remove
            if (itemsRemove.length > 0) {
                return prevList.filter((user) => !itemsRemove.includes(user.adminId));
            }

            return prevList;
        });
    };

    // --- Khi đổi page ---
    const onPaginationChange: PaginationProps['onChange'] = (pageNumber, pageSize) => {
        gListUserAccount(pageSize, pageNumber);
    };

    // --- Khôi phục selection khi data mới load ---
    useEffect(() => {
        if (gridApiRef.current && listUserAccount && listUserAccount.length > 0) {
            isRestoringSelection.current = true;

            gridApiRef.current.forEachNode((node: any) => {
                if (node.data && selectedIds.has(Number(node.data.id))) {
                    node.setSelected(true, false, true);
                } else {
                    node.setSelected(false, false, true);
                }
            });

            setTimeout(() => {
                isRestoringSelection.current = false;
            }, 0);
        }
    }, [listUserAccount, selectedIds]);

    useEffect(() => {
        if (inforGroup) {
            if (inforGroup?.status === GROUPTYPE.SHARED) {
                const adminIds = new Set(
                    inforGroup.listAdmin.map(item => item.adminId)
                );

                setSelectedIds(adminIds);
                setDataFilterList(dataDefaulFilterList)
                setSelectedIdAccount(inforGroup.listAdmin)

                // Populate permissionByUserId từ dữ liệu hiện có
                const permMap: Record<string, PERMISSION> = {}
                inforGroup.listAdmin.forEach(item => {
                    permMap[String(item.adminId)] = item.permissionType
                })
                setPermissionByUserId(permMap)
            }
        } else {
            setDataFilterList(dataDefaulFilterList)
            setSelectedIds(new Set())
            setSelectedIdAccount([])
            setPermissionByUserId({})
        }
    }, [inforGroup?.listAdmin]);

    useEffect(() => {
        if (inforGroup?.status === GROUPTYPE.PRIVATE) {
            //Xóa ds phân quyền đã chọn
            setSelectedIds(new Set())
            setSelectedIdAccount([])

        }
    }, [inforGroup?.status]);

    useEffect(() => {
        gListUserAccount()
    }, [dataFilterList])



    return (
        <div className="custom-page-css w-full flex flex-col gap-2 bg-white rounded">
            {/* Filters */}
            <div className="custom-page-css flex items-start gap-2">
                <div className="custom-page-css h-fit flex-1 d-flex  gap-2 overflow-hidden">
                    <div className="w-full  d-flex align-center  justify-between overflow-hidden">

                        <TreeSelect
                            className={'custom-scroll-multi-select custom-page-css w-full border rounded overflow-hidden'}
                            multiple
                            showSearch
                            placeholder="Chọn đơn vị"
                            allowClear
                            value={dataFilterList?.listUnit}
                            treeDefaultExpandAll={false}
                            treeData={sLNationwide}
                            filterTreeNode={(input: string, option: any) =>
                                typeof option?.label === 'string' && option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            onChange={(e) => setDataFilterList({ ...dataFilterList, listUnit: e })}
                        />
                    </div>
                </div>
                <div className="custom-page-css h-fit flex-2 border-F2F4F7 bg-FCFCFD rounded-2 d-flex align-center justify-between overflow-hidden">
                    <Input
                        placeholder="Tìm kiếm tài khoản người dùng"
                        className="custom-input-css custom-page-css border-none outline-none"
                        value={dataFilterList.search}
                        onChange={(e) => setDataFilterList({ ...dataFilterList, search: e.target.value })}
                    />
                    <div className=" border-l-F2F4F7 h-full d-flex align-center">
                        <SearchOutlined className="text-sm text-98A2B3 px-9-px" />
                    </div>
                </div>
            </div>
            <div className="custom-page-css flex items-center justify-between gap-1">
                <div className="custom-page-css flex items-center gap-1">
                    <span className='custom-page-css text-base text-4B4B4B font-regular'>Đã chọn:</span>
                    <span className='custom-page-css text-base text-344054 font-regular'>{formatPrice(selectedIds.size)} khách hàng</span>
                </div>
                <div className="custom-page-css flex items-center justify-end gap-2">
                    <div className={`${tabPermission === PERMISSION.VIEW ? 'border-40A9FF bg-E6F7FF' : 'border'} custom-page-css flex items-center justify-center py-1 px-3  rounded-3xl  cursor-pointer`}
                        onClick={() => setTabPermission(PERMISSION.VIEW)}
                    >
                        <span className={`${tabPermission === PERMISSION.VIEW ? 'text-40A9FF' : 'text-98A2B3 '} custom-page-css text-base font-regular`}>Xem</span>
                    </div>
                    <div className="custom-page-css divider"></div>
                    <div className={`${tabPermission === PERMISSION.EDIT ? 'border-40A9FF bg-E6F7FF' : 'border'} custom-page-css flex items-center justify-center py-1 px-3  rounded-3xl  cursor-pointer`}
                        onClick={() => setTabPermission(PERMISSION.EDIT)}
                    >
                        <span className={`${tabPermission === PERMISSION.EDIT ? 'text-40A9FF' : 'text-98A2B3 '} custom-page-css text-base font-regular`}>Sửa</span>
                    </div>
                    <div className="custom-page-css divider"></div>
                    <div className={`${tabPermission === PERMISSION.DELETE ? 'border-40A9FF bg-E6F7FF' : 'border'} custom-page-css flex items-center justify-center py-1 px-3  rounded-3xl  cursor-pointer`}
                        onClick={() => setTabPermission(PERMISSION.DELETE)}
                    >
                        <span className={`${tabPermission === PERMISSION.DELETE ? 'text-40A9FF' : 'text-98A2B3 '} custom-page-css text-base font-regular`}>Sửa, Xóa</span>
                    </div>
                </div>
            </div>

            <div className="ag-theme-alpine custom-table-ag">
                <AgGridReact
                    domLayout="autoHeight"
                    className="my-auto-height-grid"
                    getRowStyle={(params: any) => ({
                        backgroundColor: params?.node?.rowIndex % 2 ? '#f7f9fc' : '#ffffff',
                    })}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    rowHeight={55}
                    defaultColDef={{
                        resizable: true,
                        flex: 1,
                        valueFormatter: (params: any) =>
                            params.value && String(params.value).trim() !== ""
                                ? params.value
                                : "Không xác định",
                    }}
                    overlayNoRowsTemplate={NodataTable}
                    rowSelection={{ mode: 'multiRow' }} selectionColumnDef={{ pinned: 'left', lockPosition: 'left', suppressMovable: true }}
                    getRowId={getRowId}
                    onGridReady={onGridReady}
                    onSelectionChanged={onSelectionChanged}
                />
            </div>
            <ConfigProvider locale={viVN}>
                <div className="pagination-block">
                    <Pagination
                        showSizeChanger
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        itemRender={(page, type, originalElement) => {
                            // Chỉ custom khi là nút số trang
                            if (type === 'page') {
                                return <span>{formatNumber(page)}</span>;
                            }
                            // prev / next / jump-prev / jump-next giữ nguyên
                            return originalElement;
                        }}
                        showTotal={(total) => `Tổng cộng ${dataPagination(pagination.total, pagination.pageSize, pagination.current).totalPage} trang`}
                        className='rs-pagination'
                        onChange={onPaginationChange}
                    />
                </div>
            </ConfigProvider>
        </div>
    )
}
export default memo(EG_Decentralization)
